import { AuthApi } from '../api/auth.api';
import RootStore from './RootStore';
import { objToParams } from '../utils/url';
import axios from 'axios';
import { decodeBase64EncodeStringAndParseToJson } from '../utils/string';
import {
  getLocalStorageItem,
  LocalStorageKeyEnum,
  removeLocalStorageItem,
  setLocalStorageItem,
} from '../utils/local-storage-handler';

export default class OmniLoginStore {
  private rootStore: RootStore;

  hasOmniUpdateParameter = () => {
    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.get('omni-update')) {
      return true;
    }
    return false;
  };

  startOmniAuthFlowAndAddOmniUpdateParameterAndRedirect = async (
    path: string,
    param: string,
  ) => {
    try {
      const localToken = getLocalStorageItem(LocalStorageKeyEnum.TOKEN) || '';

      // the GET parameter "omni-update" must be given after Omni authentication. see OmniLoginStore.auth of @action
      axios.defaults.headers.common['Authorization'] = `Bearer ${localToken}`;
      window.location.replace(
        await AuthApi.getOmniAuthEndpointPath(
          this.rootStore.userStore.generateLoginParams(
            new URLSearchParams(param),
            // if path is set empty, the destination is extracted at rmp-bff.
            // see generateOmniStateByReferer function.
            path === '/login' ? '/inquiry/new' : '',
          ),
        ),
      );
    } catch (e: any) {
      console.error(e);
      removeLocalStorageItem(LocalStorageKeyEnum.TOKEN);

      // when 401, it redirects to Omni UI (configured at rmp-bff)

      if (e.status !== 401 /* NOT Unauthorized */) {
        this.rootStore.inquiryPageStore.setShowErrorPage(true);
      }
    }
  };

  extractOmniStateAndDecodeState = async () => {
    const searchParams = new URLSearchParams(window.location.search);
    console.debug(`code: ${searchParams.get('code')}`);
    console.debug(`state: ${searchParams.get('state')}`);

    // This is for local development only
    // code for example: '@St.key-for-various-tokens.M0WGyZaImDKnAr7K2yjtVdhq6Xwh7sOl.u9JezneTA3NLn7eZKrDLyYQnLTGcRMxNyteqyHOh4uIw_T1POYJJ4o0-gUhFpYCvSIQD5-KOatubx45k83v7UojSZxtvj6qpIHCLDs29erUwASyScvXMEsaYe1MwhsGWSj4fccoZffuRCYjMUyEi1UNi2uUECSc7e19os-OoNM2VzsNMi4ccLnfBDPcuA-1wjLHpwAG4xxrwZSl4gid7EHsGIXSowRqVU9UqLn2l6HSQCTMuderp7jl91aK5VRjc7O-WF_G5Wm0r0nFdIPhxeCR4xev68Opc4tK5nKLE8rw-fLek0ccb0jL5TIsah3dyfq1l3u1kncQrdmrgmVi21JeWBnQNzwN77kMNvvwaddtJJKIV1_iaPC_XmrGJWDZGOX0OLADxXW1hPWiX14LoEH4dOu12XUU2urzTTxvtIFN0l5ZLVvvAnH0gxO8m6svfhWurKFhWmDv58FtDnjem2pN8TjvH7XdIIvl_hqCzfRGE-7fcp_YK1OaHEaUJti635uTt1XiE13H_sNlN5aO0A07NRtsSIP913Ctl39hlK-CZd5dWiI1pp34tD-D3py6Cyvgeecg_fh9fnU7tiPyQbl69vRhLqPj5xW-8o2k3oCHTR2Un179eMoMWEzMdiW57QjPJTwugVS5uaYuPUKkqsfKsJiC65BXFFX26HxNFOueC6oK1Hstyv0Mg0nGmE0hgbZixJAv8lqY4VPSluXATZ37FW3bfM_d1rn4eKWaGO0p1HieCVO0O9Zt2brgQJrD4G13xXZXAk0SvckgSOEiUS3V2TqZyRk4gG8JnHKR-BM173t99O_1f7pp5-oEkn0oP8Fv4_xQ5Nbgb2qWieS8lUOKHyHQy5UvRhvoef_ICPnvnvTFleamnTkiJ5Aap_IdulnOpwgtjNJEHySJPIz4aTAw9MBdl_imQUjksgB5-yeCLz28EKdUpPsVh3slFLleuNLl3ssSQwxIlZsoGA738tBaDmZROH_8ENPTF529R56hMbMh0htGkrioTK_AjeAXhlQojhIsm5wSrmhXfuXhCWN3sL90m_O3TBDrOda_2fC0nKS_DphK4t_I2EBnrjbh82piqIewlZ4fChmN5dJ4F5jQGJgLN8jRuMs-Er15U0E4afeW3upaGn4tew-o9IA';
    const codeForDebug = '';

    // This is for local development only
    // state for example: 'eyJzZXJ2aWNlSWQiOjEwMDMsInNlcnZpY2VBdHRyaWJ1dGUiOjEwNCwicmVjaXBpZW50cyI6W3sic2VydmljZUlkIjoxMDAzLCJzZXJ2aWNlQXR0cmlidXRlIjoxMDYsImF1dGhJZCI6IjEwMjkwNiJ9XSwiY2F0ZWdvcnlJZCI6MSwidGFyZ2V0SWQiOiJ1bmRlZmluZWQiLCJ0YXJnZXROYW1lIjoidW5kZWZpbmVkIiwidGFyZ2V0VXJsIjoidW5kZWZpbmVkIiwiZGVzdGluYXRpb24iOiIvaW5xdWlyeS9uZXcifQ';
    const encodedStateForDebug = '';

    return {
      code: codeForDebug || searchParams.get('code')!,
      decodedState: await decodeBase64EncodeStringAndParseToJson(
        // this is special treatment. Omni provides '+' character as '%20', which is half-space.
        // the half-space causes failure at decoding. so here it replaces the space to the original character of '+'.
        encodedStateForDebug || searchParams.get('state')!.replaceAll(' ', '+'),
      ),
    };
  };

  removeOmniUpdateParameter = () => {
    const url = new URL(window.location.href);
    const params = new URLSearchParams(url.search);
    params.delete('omni-update');

    const remainingParameter =
      Array.from(params).length === 0
        ? url.pathname
        : `${url.pathname}?${objToParams(
            Object.fromEntries(
              new Map([...params].map((param) => [param[0], param[1]])),
            ),
          )}`;

    window.history.replaceState(null, '', remainingParameter);
  };

  // after Omni authentication, this action is executed first in OmniLoginPage.tsx. then JWT is created.
  auth = async () => {
    console.debug('enter auth');
    const loginParams =
      await this.rootStore.userStore.getLoginParamsFromEncodedState();
    console.debug('loginParams: ');
    console.debug(loginParams);
    // in case of unexpected inflow, serviceId and serviceAttribute are set -1 by rmp-bff
    if (loginParams.serviceId > 0 && loginParams.serviceAttribute > 0) {
      try {
        const jwt = await AuthApi.omniLogin(loginParams);
        const destination = loginParams.destination;
        setLocalStorageItem(LocalStorageKeyEnum.TOKEN, jwt);
        // just in case of destination is not set... (should never happen in valid state value)
        window.location.href =
          (!destination ? '/' : destination) + '?omni-update=done';
      } catch (e: any) {
        console.error('Unable to get JWT');
        console.error(e);
        removeLocalStorageItem(LocalStorageKeyEnum.TOKEN);
        removeLocalStorageItem(LocalStorageKeyEnum.STATE);
        if (e.status === 400) {
          console.error(e.data);
          window.location.href = '/404';
          return;
        }
        // redirect to inquiry list so that user can start Omni again
        window.location.href = `/inquiry?serviceId=${loginParams.serviceId}&serviceAttribute=${loginParams.serviceAttribute}`;
      }
    } else {
      window.location.href = '/404';
    }
  };

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
  }
}
